/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'sarsaparilla';
import { add, differenceInDays, format, isSameDay, startOfDay, toDate } from 'date-fns';
import * as types from '../constants';

const DATE_RANGE_SIZE = 6;

const propTypes = {
    activeDate: PropTypes.string,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    onDateSelected: PropTypes.func,
};

export default class TripDatePager extends React.Component {
    static propTypes = propTypes;

    constructor(props) {
        super(props);

        this.state = {
            offset: 0,
            activeDate: null,
            startDate: new Date(),
            endDate: add(new Date(), { days: DATE_RANGE_SIZE }),
        };

        this.onPrevious = this.onPrevious.bind(this);
        this.onNext = this.onNext.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const nextState = {};

        if (nextProps.startDate) {
            nextState.startDate = nextProps.startDate;
        }
        if (nextProps.endDate) {
            nextState.endDate = nextProps.endDate;
        }
        if (nextProps.activeDate) {
            const activeDate = startOfDay(nextProps.activeDate);
            nextState.activeDate = activeDate;
        }
        if (nextState.startDate && nextState.endDate) {
            if (nextState.activeDate) {
                const diff = differenceInDays(nextState.activeDate, nextState.startDate);
                nextState.offset = DATE_RANGE_SIZE * Math.floor(diff / DATE_RANGE_SIZE);
            }

            if (
                differenceInDays(nextState.startDate, this.state.startDate) !== 0 ||
                differenceInDays(nextState.endDate, this.state.endDate) !== 0
            ) {
                nextState.offset = 0;
            }

            this.setState(nextState);
        }
    }

    onPrevious() {
        const start = add(toDate(this.state.startDate), {
            days: this.state.offset - DATE_RANGE_SIZE,
        });
        const diff = differenceInDays(start, this.state.startDate);

        if (diff < 0) {
            this.setState({
                offset: 0,
            });
        } else {
            this.setState({
                offset: this.state.offset - DATE_RANGE_SIZE - 1,
            });
        }
    }

    onNext() {
        const start = add(toDate(this.state.startDate), {
            days: this.state.offset + DATE_RANGE_SIZE,
        });
        const diff = differenceInDays(start, this.state.endDate);
        if (diff < 0) {
            // make sure to always show full page of dates
            let size = DATE_RANGE_SIZE;
            if (Math.abs(diff) + 1 < DATE_RANGE_SIZE) {
                size = DATE_RANGE_SIZE - (Math.abs(diff) + 1);
            }
            this.setState({
                offset: this.state.offset + size + 1,
            });
        }
    }

    onDateSelected(item) {
        this.setState({
            activeDate: item.date,
        });
        if (this.props.onDateSelected) {
            this.props.onDateSelected(item.date);
        }
    }

    getDateRange(startDate, endDate, offset) {
        let hasPrevious = false;
        let hasNext = false;

        let newStartDate = startDate;
        let newEndDate = endDate;

        // calculate start end date with scroll offset
        if (offset !== 0) {
            const start = add(toDate(startDate), { days: offset });
            if (differenceInDays(start, startDate) > 0) {
                newStartDate = start;
                hasPrevious = true;
            }
        }

        // calculate end date base for date page size
        {
            const end = add(toDate(newStartDate), { days: DATE_RANGE_SIZE });

            if (differenceInDays(endDate, end) > 0) {
                newEndDate = end;
                hasNext = true;
            }
        }

        // calculate date range to display
        const diff = differenceInDays(newEndDate, newStartDate);
        const ret = [];

        for (let i = 0; i <= diff; i += 1) {
            const dt = add(toDate(newStartDate), { days: i });
            ret[ret.length] = {
                date: dt,
                label: format(dt, types.FNS_LL_DATE_FORMAT),
                month: format(dt, 'MMM').toLowerCase(),
                day: format(dt, 'd'),
            };
        }

        return {
            range: ret,
            hasNext,
            hasPrevious,
        };
    }

    render() {
        // construct dates to render
        const dateRangeObj = this.getDateRange(
            this.state.startDate,
            this.state.endDate,
            this.state.offset
        );

        const dates = [];
        dateRangeObj.range.forEach((item) => {
            let className = '';
            if (this.state.activeDate && isSameDay(this.state.activeDate, item.date)) {
                className = 'active';
            }
            dates[dates.length] = (
                <button
                    type="button"
                    key={`date-item-${item.month}-${item.day}`}
                    className={className}
                    onClick={this.onDateSelected.bind(this, item)}
                    aria-label={`Jump to ${item.label}`}
                    title={item.label}
                >
                    <span className="rec-month">{item.month}</span>
                    <span className="rec-day">{item.day}</span>
                </button>
            );
        });

        return (
            <div
                className="trip-planner-date-pager"
                data-testid="trip-planner-date-pager"
            >
                <div className="trip-planner-date-blocks">
                    <button
                        type="button"
                        disabled={!dateRangeObj.hasPrevious}
                        onClick={this.onPrevious}
                        aria-label="Previous 7 days"
                    >
                        <Icon iconName="arrow-back" />
                    </button>

                    {dates}

                    <button
                        type="button"
                        disabled={!dateRangeObj.hasNext}
                        onClick={this.onNext}
                        aria-label="Next 7 days"
                    >
                        <Icon iconName="arrow-forward" />
                    </button>
                </div>
            </div>
        );
    }
}
